import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import {
  useLocalStorage,
  useLocalStorageState,
} from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { chatRouter } from "@editor/trpc/chat";
import SvgMessages from "@svg/messages";

import {
  useActiveTriggerJobChats,
  useStreamingChats,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { skipToken } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { z } from "zod";

const PAGE_SIZE = 50;

function ChatItemSkeleton() {
  return (
    <div className="w-full py-3 px-3">
      <Skeleton
        className="h-4 w-3/4"
        speed={2}
        backgroundColor="#f1f5f9"
        foregroundColor="#e2e8f0"
      />
    </div>
  );
}

function ChatTitleSkeleton() {
  return (
    <Skeleton
      className="h-5 w-20"
      speed={2}
      backgroundColor="#f1f5f9"
      foregroundColor="#e2e8f0"
    />
  );
}

function ManageChats() {
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const storage = useLocalStorage();
  const streamingChats = useStreamingChats();
  const activeTriggerJobs = useActiveTriggerJobChats();
  const [, setIsChatSidebarOpen] = useLocalStorageState(
    "replo.editor.chatSidebarOpen",
    false,
    { schema: z.boolean() },
  );

  const {
    data: chatsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
  } = chatRouter.getPaginatedChats.useInfiniteQuery(
    projectId
      ? {
          projectId,
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const chats = React.useMemo(
    () => chatsData?.pages?.flatMap((page) => page.chats) ?? [],
    [chatsData],
  );

  useOnValueChange(chatsData, (oldValue, newValue) => {
    const stored = storage.getItem("replo.editor.chatSidebarOpen");
    const prevCount = oldValue?.pages?.[0]?.chats?.length ?? 0;
    const nextCount = newValue?.pages?.[0]?.chats?.length ?? 0;
    if (stored != null) {
      return;
    }

    if (prevCount === 0 && nextCount > 0) {
      setIsChatSidebarOpen(true);
    }
  });

  const handleNewChat = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", "");
      return newParams;
    });
    setIsChatSidebarOpen(false);
    logEvent("chat.new");
  };

  const handleChatClick = (chatId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", chatId);
      return newParams;
    });
  };

  if (!projectId || !user?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner variant="primary" size={24} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        Error Loading Chats
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0 w-full relative">
        <div className="px-3 pt-4 pb-4">
          <div className="mb-4">
            <ChatTitleSkeleton />
          </div>
          <Skeleton
            className="h-9 w-full"
            speed={2}
            backgroundColor="#f1f5f9"
            foregroundColor="#e2e8f0"
          />
        </div>
        <div className="flex-1 px-3 pb-3 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <ChatItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 p-3 flex-col h-full min-h-0 w-full bg-sheet relative ">
      <h2 className="text-base font-semibold text-default m-0 mb-4">
        All Chats
      </h2>
      {chats.length > 0 && (
        <Button
          variant="secondary"
          size="base"
          layoutClassName="w-full mb-1"
          UNSAFE_className="rounded-md"
          onClick={handleNewChat}
        >
          New Chat
        </Button>
      )}
      <div
        id="chats-scroll-container"
        className="flex-1 overflow-y-auto no-scrollbar"
      >
        {chats.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center p-4">
            <EmptyState
              variant="noRings"
              image={<SvgMessages className="h-16 w-auto" />}
              title="No chats yet"
              description="Chat history will show up here"
              actionButtonProps={{
                variant: "secondary",
                size: "base",
                layoutClassName: "mt-2",
                onClick: handleNewChat,
                children: "Create new chat",
              }}
            />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={chats.length}
            next={() => void fetchNextPage()}
            hasMore={hasNextPage ?? false}
            scrollableTarget="chats-scroll-container"
            loader={<ChatItemSkeleton />}
          >
            <div className="flex flex-col gap-2 no-scrollbar">
              {chats.map((chat) => {
                const isStreaming = streamingChats.includes(chat.id);
                const hasRunningPageGenerationJob = activeTriggerJobs.includes(
                  chat.id,
                );

                return (
                  <MenuItem
                    key={chat.id}
                    variant="default"
                    size="base"
                    selected={chatId === chat.id}
                    layoutClassName="w-full"
                    UNSAFE_className="h-8 py-1 px-2 [&_.lucide-check]:hidden rounded-md"
                    onClick={() => handleChatClick(chat.id)}
                  >
                    <div className="flex flex-row items-center gap-2">
                      {(isStreaming || hasRunningPageGenerationJob) && (
                        <div className="flex-shrink-0">
                          <Spinner variant="primary" size={12} />
                        </div>
                      )}
                      <div className="whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                        {chat.title}
                      </div>
                    </div>
                  </MenuItem>
                );
              })}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}

export default ManageChats;
